import { Coords } from '@energy-stacks/fleet/shared';
import { Box, useTheme } from '@mui/material';
import { memo, useEffect, useRef } from 'react';

type TourJobMarkerProps = Coords & {
  jobVisitOrders: number[];
  isHighlighted: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

const WIDTH_CLUSTER = 21;
const WIDTH_SINGLE = 36;
const HEIGHT = 28;

export const TourJobMarker: React.FC<TourJobMarkerProps> = memo(
  ({ jobVisitOrders, isHighlighted, onClick }) => {
    const itemWidth =
      jobVisitOrders.length === 1 ? WIDTH_SINGLE : WIDTH_CLUSTER;
    const theme = useTheme();
    const totalWidth = jobVisitOrders.length * itemWidth;

    const markerRef = useRef<HTMLDivElement | null>(null);
    const frameRef = useRef<number>();

    const baseTransform = 'translate(-50%, -100%)';

    useEffect(() => {
      if (!isHighlighted) {
        if (markerRef.current) {
          markerRef.current.style.transform = baseTransform;
        }

        if (frameRef.current) {
          cancelAnimationFrame(frameRef.current);
        }
        return;
      }

      let start: number | null = null;

      const animate = (time: number) => {
        if (start === null) {
          start = time;
        }

        const progress = (time - start) / 1000;
        const value = Math.sin(progress * Math.PI * 2) * 10;

        if (markerRef.current) {
          markerRef.current.style.transform = `${baseTransform} translateY(${value}px)`;
        }

        frameRef.current = requestAnimationFrame(animate);
      };

      frameRef.current = requestAnimationFrame(animate);

      return () => {
        if (frameRef.current) {
          cancelAnimationFrame(frameRef.current);
        }
      };
    }, [isHighlighted]);

    return (
      <Box
        ref={markerRef}
        onClick={onClick}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          transform: baseTransform,
          '&:hover': {
            transform: `${baseTransform} translate(0, -2px)`,
            transition: '0.05s linear',
          },
          zIndex: 1,
        }}
      >
        <svg width={totalWidth} height={HEIGHT + 10}>
          <rect
            x={0}
            y={0}
            width={totalWidth}
            height={HEIGHT}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fill={theme.palette.dark.main}
            rx={8}
            ry={8}
          />

          {/* Vertical dividers */}
          {jobVisitOrders.slice(0, -1).map((order, i) => {
            const x = (i + 1) * itemWidth;

            return (
              <line
                key={`divider-${order}`}
                x1={x}
                y1={0}
                x2={x}
                y2={HEIGHT}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                stroke={theme.palette.dark[600]}
              />
            );
          })}

          {/* Labels */}
          {jobVisitOrders.map((visitOrder, i) => {
            const x = i * itemWidth + itemWidth / 2;

            return (
              <text
                key={`label=${visitOrder}`}
                x={x}
                y={HEIGHT / 2 + 5}
                textAnchor="middle"
                fontSize={14}
                fill={theme.palette.background.paper}
              >
                {visitOrder}
              </text>
            );
          })}

          {/* Bottom pin triangle */}
          <polygon
            points={`
            ${totalWidth / 2 - 7},${HEIGHT}
            ${totalWidth / 2 + 7},${HEIGHT}
            ${totalWidth / 2},${HEIGHT + 5}
          `}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fill={theme.palette.dark.main}
          />
        </svg>
      </Box>
    );
  }
);
